import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, prefersReducedMotion } from '@theme/utilities';

/**
 * PostHog tracking helper. No-ops if PostHog is not loaded or in design mode.
 */
const track = {
  /** @param {string} event @param {Record<string, unknown>} [props] @param {Record<string, unknown>} [options] */
  capture(event, props, options) {
    if (window.Shopify?.designMode) return;
    window.posthog?.capture(event, props, options);
  },
};

/**
 * A drawer menu panel with submenu support.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} backdrop - The backdrop overlay.
 * @property {HTMLElement} panel - The sliding panel.
 * @property {HTMLElement[]} [submenu] - Submenu panels.
 *
 * @extends {Component<Refs>}
 */
class DrawerMenu extends Component {
  requiredRefs = ['dialog', 'backdrop', 'panel'];

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {number} */
  #submenuZIndex = 0;

  /** @type {HTMLElement | null} */
  #triggerElement = null;

  /** @type {Element | null} */
  #sectionElement = null;

  // ── Analytics state ──
  /** @type {number} */
  #openedAt = 0;

  /** @type {number} */
  #itemClicks = 0;

  /** @type {number} */
  #submenusVisited = 0;

  /** @type {string} */
  #closeMethod = 'unknown';

  /** @type {boolean} */
  #closeTracked = false;

  /** @type {Map<string, number>} submenu handle → opened timestamp */
  #submenuTimers = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.refs.dialog.addEventListener('click', this.#onBackdropClick);
    this.refs.panel.addEventListener('click', this.#onItemClick);

    this.#assignItemPositions();

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.#onEditorBlockSelect);
      this.#sectionElement = this.closest('.shopify-section');
      this.#sectionElement?.addEventListener(
        'shopify:section:select',
        this.#onEditorSectionSelect
      );
    }
  }

  /**
   * Compute and assign data-sm-position to all trackable items within each
   * nav container (root page + each submenu page) independently.
   */
  #assignItemPositions() {
    const containers = this.querySelectorAll('nav');
    containers.forEach((nav) => {
      const items = nav.querySelectorAll('[data-sm-type]');
      items.forEach((el, i) => {
        /** @type {HTMLElement} */ (el).dataset.smPosition = String(i);
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.refs.dialog.removeEventListener('click', this.#onBackdropClick);
    this.refs.panel.removeEventListener('click', this.#onItemClick);

    if (this.#sectionElement) {
      this.#sectionElement.removeEventListener(
        'shopify:section:select',
        this.#onEditorSectionSelect
      );
      this.#sectionElement = null;
    }
  }

  /** @returns {boolean} */
  get isOpen() {
    return this.refs.dialog.open;
  }

  /**
   * Open the drawer menu.
   */
  open() {
    const { dialog, panel } = this.refs;
    if (dialog.open) return;

    this.#triggerElement = /** @type {HTMLElement | null} */ (document.activeElement);

    // Analytics: reset session state
    this.#openedAt = Date.now();
    this.#itemClicks = 0;
    this.#submenusVisited = 0;
    this.#closeMethod = 'unknown';
    this.#closeTracked = false;
    this.#submenuTimers.clear();

    track.capture('menu_open');

    this.#lockScroll();

    if (window.Shopify?.designMode) {
      dialog.show();
    } else {
      dialog.showModal();
    }

    if (prefersReducedMotion()) {
      this.refs.backdrop.classList.add('opacity-100');
      trapFocus(dialog);
      return;
    }

    panel.style.willChange = 'transform';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.refs.backdrop.classList.add('opacity-100');
        panel.classList.add('drawer-menu__panel--open');
        onAnimationEnd(
          panel,
          () => {
            panel.style.willChange = '';
            trapFocus(dialog);
          },
          { subtree: false }
        );
      });
    });
  }

  /**
   * Close the drawer menu.
   */
  close = async () => {
    const { dialog, panel } = this.refs;
    if (!dialog.open) return;

    // Analytics: track close (guarded to prevent double-fire on navigation)
    if (!this.#closeTracked) {
      this.#closeTracked = true;
      const timeOpenMs = Date.now() - this.#openedAt;
      const isBounce =
        timeOpenMs < 2000 &&
        this.#itemClicks === 0 &&
        this.#submenusVisited === 0;

      track.capture('menu_close', {
        close_method: this.#closeMethod,
        time_open_ms: timeOpenMs,
        submenus_visited: this.#submenusVisited,
        items_clicked: this.#itemClicks,
      });

      if (isBounce) {
        track.capture('menu_bounce', { time_open_ms: timeOpenMs });
      }
    }

    removeTrapFocus();

    this.refs.backdrop.classList.remove('opacity-100');

    if (prefersReducedMotion()) {
      dialog.close();
      this.#closeAllSubmenus();
      this.#unlockScroll();
      this.#triggerElement?.focus();
      this.#triggerElement = null;
      return;
    }

    panel.style.willChange = 'transform';
    panel.classList.remove('drawer-menu__panel--open');

    await onAnimationEnd(panel, undefined, { subtree: false });

    panel.style.willChange = '';
    dialog.close();
    this.#closeAllSubmenus();
    this.#unlockScroll();
    this.#triggerElement?.focus();
    this.#triggerElement = null;
  };

  /**
   * Close the drawer menu (triggered by close button).
   */
  closeViaButton() {
    this.#closeMethod = 'button';
    return this.close();
  }

  /**
   * Toggle the drawer menu.
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open a submenu by name.
   * @param {{ name: string }} data
   */
  openSubmenu({ name }) {
    const submenu = this.refs.submenu?.find(
      (el) => el.dataset.submenu === name
    );
    if (!submenu) return;

    this.#submenuZIndex++;
    submenu.style.zIndex = String(this.#submenuZIndex);
    submenu.style.willChange = 'transform';
    submenu.classList.add('drawer-menu__submenu--open');

    // Analytics
    this.#submenusVisited++;
    this.#submenuTimers.set(name, Date.now());
    track.capture('menu_submenu_open', {
      submenu_handle: name,
      depth: this.#submenuTimers.size,
    });

    // Focus the back button inside the submenu
    const backButton = submenu.querySelector('button');
    onAnimationEnd(
      submenu,
      () => {
        submenu.style.willChange = '';
        backButton?.focus();
      },
      { subtree: false }
    );
  }

  /**
   * Close a submenu by name.
   * @param {{ name: string }} data
   */
  closeSubmenu({ name }) {
    const submenu = this.refs.submenu?.find(
      (el) => el.dataset.submenu === name
    );
    if (!submenu) return;

    // Analytics
    const openedAt = this.#submenuTimers.get(name);
    track.capture('menu_submenu_back', {
      submenu_handle: name,
      time_in_submenu_ms: openedAt ? Date.now() - openedAt : undefined,
    });
    this.#submenuTimers.delete(name);

    submenu.style.willChange = 'transform';
    submenu.classList.remove('drawer-menu__submenu--open');
    onAnimationEnd(
      submenu,
      () => {
        submenu.style.willChange = '';
        submenu.style.zIndex = '';
      },
      { subtree: false }
    );
  }

  /** @param {Event} event */
  #onEditorBlockSelect = (event) => {
    if (!this.isOpen) this.open();

    if (event.target instanceof HTMLElement) {
      const submenuPanel = event.target.closest('[data-submenu]');
      this.#closeAllSubmenus();

      if (submenuPanel instanceof HTMLElement && submenuPanel.dataset.submenu) {
        this.openSubmenu({ name: submenuPanel.dataset.submenu });
      }
    }
  };

  #onEditorSectionSelect = () => {
    if (!this.isOpen) this.open();
  };

  #closeAllSubmenus() {
    this.refs.submenu?.forEach((el) => {
      el.classList.remove('drawer-menu__submenu--open');
      el.style.zIndex = '';
      el.style.willChange = '';
    });
    this.#submenuZIndex = 0;
    this.#submenuTimers.clear();
  }

  #lockScroll() {
    this.#previousScrollY = window.scrollY;
    Object.assign(document.body.style, {
      width: '100%',
      position: 'fixed',
      top: `-${this.#previousScrollY}px`,
    });
  }

  #unlockScroll() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /** @param {MouseEvent} event */
  #onBackdropClick = (event) => {
    if (event.target === this.refs.backdrop) {
      this.#closeMethod = 'backdrop';
      this.close();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeMethod = 'escape';
      this.close();
    }
  };

  /**
   * Delegated click handler for menu item tracking.
   * @param {MouseEvent} event
   */
  #onItemClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const anchor = target.closest('a[href]');
    if (!anchor) return;

    this.#itemClicks++;
    this.#closeMethod = 'navigation';

    // Walk up to find the <li> with data-sm-* attributes
    const item = anchor.closest('[data-sm-type]');
    const submenuPanel = anchor.closest('[data-submenu]');

    track.capture(
      'menu_item_click',
      {
        item_type: item?.dataset.smType ?? 'unknown',
        item_title: item?.dataset.smTitle ?? anchor.textContent?.trim() ?? '',
        item_url: anchor.getAttribute('href') ?? '',
        item_position: item?.dataset.smPosition
          ? Number(item.dataset.smPosition)
          : undefined,
        menu_level: submenuPanel?.dataset.submenu ?? 'root',
        has_badge: item?.dataset.smBadge === 'true',
      },
      { transport: 'sendBeacon' }
    );

    // Fire menu_close now — the page will unload before close() runs.
    this.#closeTracked = true;
    const timeOpenMs = Date.now() - this.#openedAt;

    track.capture(
      'menu_close',
      {
        close_method: 'navigation',
        time_open_ms: timeOpenMs,
        submenus_visited: this.#submenusVisited,
        items_clicked: this.#itemClicks,
      },
      { transport: 'sendBeacon' }
    );
  };
}

if (!customElements.get('drawer-menu')) {
  customElements.define('drawer-menu', DrawerMenu);
}
